import { getSubclasses, normalizeBrickClass, getDisplayName } from '@/data/brickHierarchy';

/**
 * BRICK Schema Class Options Composable
 *
 * Provides all BRICK 1.4 compliant class options for buildings, levels, spaces,
 * equipment, devices, and points. Also includes service types, controller types,
 * and zone section options.
 *
 * @returns {Object} All BRICK class option arrays and related configuration options
 */
export function useBrickClassOptions() {
  // Building BRICK class options - all valid BRICK v1.4 classes
  const buildingBrickClasses = [
    { label: 'Building', value: 'brick:Building' },
    { label: 'Hospital', value: 'brick:Hospital' },
    { label: 'Office Building', value: 'brick:Office_Building' },
    { label: 'Data Center', value: 'brick:Data_Center' },
    { label: 'Warehouse', value: 'brick:Warehouse' },
    { label: 'Retail Building', value: 'brick:Retail_Building' },
    { label: 'Hotel Building', value: 'brick:Hotel_Building' },
    { label: 'Educational Building', value: 'brick:Educational_Building' },
  ];

  // Level/Floor BRICK class options
  const levelBrickClasses = [
    { label: 'Floor', value: 'brick:Floor' },
    { label: 'Basement', value: 'brick:Basement' },
    { label: 'Roof', value: 'brick:Roof' },
    { label: 'Mezzanine', value: 'brick:Mezzanine' },
    { label: 'Penthouse', value: 'brick:Penthouse' },
    { label: 'Storey', value: 'brick:Storey' },
  ];

  // Space/Room BRICK class options
  const spaceBrickClasses = [
    { label: 'Space', value: 'brick:Space' },
    { label: 'Room', value: 'brick:Room' },
    { label: 'Plant Room', value: 'brick:Plant_Room' },
    { label: 'Mechanical Room', value: 'brick:Mechanical_Room' },
    { label: 'Electrical Room', value: 'brick:Electrical_Room' },
    { label: 'Server Room', value: 'brick:Server_Room' },
    { label: 'Office', value: 'brick:Office' },
    { label: 'Conference Room', value: 'brick:Conference_Room' },
    { label: 'Storage Room', value: 'brick:Storage_Room' },
    { label: 'Bathroom', value: 'brick:Bathroom' },
    { label: 'Kitchen', value: 'brick:Kitchen' },
    { label: 'Lobby', value: 'brick:Lobby' },
    { label: 'Corridor', value: 'brick:Corridor' },
    { label: 'Laboratory', value: 'brick:Laboratory' },
    { label: 'Workshop', value: 'brick:Workshop' },
    { label: 'Parking Space', value: 'brick:Parking_Space' },
    { label: 'HVAC Zone', value: 'brick:HVAC_Zone' },
    { label: 'Lighting Zone', value: 'brick:Lighting_Zone' },
    { label: 'Fire Zone', value: 'brick:Fire_Zone' },
  ];

  // Equipment BRICK class options
  const equipmentBrickClasses = [
    { label: 'Air Handling Unit', value: 'brick:Air_Handling_Unit' },
    { label: 'Variable Air Volume Box', value: 'brick:Variable_Air_Volume_Box' },
    { label: 'Fan Coil Unit', value: 'brick:Fan_Coil_Unit' },
    { label: 'Chiller', value: 'brick:Chiller' },
    { label: 'Boiler', value: 'brick:Boiler' },
    { label: 'Heat Pump', value: 'brick:Heat_Pump' },
    { label: 'Cooling Tower', value: 'brick:Cooling_Tower' },
    { label: 'Fan', value: 'brick:Fan' },
    { label: 'Pump', value: 'brick:Pump' },
    { label: 'Damper', value: 'brick:Damper' },
    { label: 'Valve', value: 'brick:Valve' },
    { label: 'Heat Exchanger', value: 'brick:Heat_Exchanger' },
    { label: 'Filter', value: 'brick:Filter' },
    { label: 'Switchgear', value: 'brick:Switchgear' },
    { label: 'Distribution Board', value: 'brick:Distribution_Board' },
    { label: 'Transformer', value: 'brick:Transformer' },
    { label: 'Generator', value: 'brick:Generator' },
    { label: 'UPS', value: 'brick:UPS' },
    { label: 'Battery', value: 'brick:Battery' },
    { label: 'Lighting Equipment', value: 'brick:Lighting_Equipment' },
    { label: 'Sensor', value: 'brick:Sensor' },
    { label: 'Controller', value: 'brick:Controller' },
    { label: 'Meter', value: 'brick:Meter' },
  ];

  // Point BRICK classes - comprehensive BRICK 1.4 classes
  const pointBrickClasses = [
    // === TEMPERATURE SENSORS ===
    { label: 'Temperature Sensor', value: 'brick:Temperature_Sensor' },
    // Air Temperature Sensors
    { label: 'Air Temperature Sensor', value: 'brick:Air_Temperature_Sensor' },
    { label: 'Discharge Air Temperature Sensor', value: 'brick:Discharge_Air_Temperature_Sensor' },
    { label: 'Mixed Air Temperature Sensor', value: 'brick:Mixed_Air_Temperature_Sensor' },
    { label: 'Outside Air Temperature Sensor', value: 'brick:Outside_Air_Temperature_Sensor' },
    { label: 'Return Air Temperature Sensor', value: 'brick:Return_Air_Temperature_Sensor' },
    { label: 'Supply Air Temperature Sensor', value: 'brick:Supply_Air_Temperature_Sensor' },
    { label: 'Zone Air Temperature Sensor', value: 'brick:Zone_Air_Temperature_Sensor' },
    { label: 'Exhaust Air Temperature Sensor', value: 'brick:Exhaust_Air_Temperature_Sensor' },
    // Water Temperature Sensors
    { label: 'Water Temperature Sensor', value: 'brick:Water_Temperature_Sensor' },
    { label: 'Chilled Water Temperature Sensor', value: 'brick:Chilled_Water_Temperature_Sensor' },
    { label: 'Chilled Water Supply Temperature Sensor', value: 'brick:Chilled_Water_Supply_Temperature_Sensor' },
    { label: 'Chilled Water Return Temperature Sensor', value: 'brick:Chilled_Water_Return_Temperature_Sensor' },
    { label: 'Hot Water Temperature Sensor', value: 'brick:Hot_Water_Temperature_Sensor' },
    { label: 'Hot Water Supply Temperature Sensor', value: 'brick:Hot_Water_Supply_Temperature_Sensor' },
    { label: 'Hot Water Return Temperature Sensor', value: 'brick:Hot_Water_Return_Temperature_Sensor' },
    { label: 'Condenser Water Temperature Sensor', value: 'brick:Condenser_Water_Temperature_Sensor' },
    { label: 'Entering Water Temperature Sensor', value: 'brick:Entering_Water_Temperature_Sensor' },
    { label: 'Leaving Water Temperature Sensor', value: 'brick:Leaving_Water_Temperature_Sensor' },

    // === PRESSURE SENSORS ===
    { label: 'Pressure Sensor', value: 'brick:Pressure_Sensor' },
    { label: 'Differential Pressure Sensor', value: 'brick:Differential_Pressure_Sensor' },
    // Air Pressure Sensors
    { label: 'Air Pressure Sensor', value: 'brick:Air_Pressure_Sensor' },
    { label: 'Air Static Pressure Sensor', value: 'brick:Air_Static_Pressure_Sensor' },
    { label: 'Air Differential Pressure Sensor', value: 'brick:Air_Differential_Pressure_Sensor' },
    { label: 'Supply Air Static Pressure Sensor', value: 'brick:Supply_Air_Static_Pressure_Sensor' },
    { label: 'Discharge Air Static Pressure Sensor', value: 'brick:Discharge_Air_Static_Pressure_Sensor' },
    { label: 'Return Air Static Pressure Sensor', value: 'brick:Return_Air_Static_Pressure_Sensor' },
    { label: 'Exhaust Air Static Pressure Sensor', value: 'brick:Exhaust_Air_Static_Pressure_Sensor' },
    { label: 'Building Air Static Pressure Sensor', value: 'brick:Building_Air_Static_Pressure_Sensor' },
    { label: 'Filter Differential Pressure Sensor', value: 'brick:Filter_Differential_Pressure_Sensor' },
    // Water Pressure Sensors
    { label: 'Water Pressure Sensor', value: 'brick:Water_Pressure_Sensor' },
    { label: 'Chilled Water Differential Pressure Sensor', value: 'brick:Chilled_Water_Differential_Pressure_Sensor' },
    { label: 'Hot Water Differential Pressure Sensor', value: 'brick:Hot_Water_Differential_Pressure_Sensor' },

    // === FLOW SENSORS ===
    { label: 'Flow Sensor', value: 'brick:Flow_Sensor' },
    // Air Flow Sensors
    { label: 'Air Flow Sensor', value: 'brick:Air_Flow_Sensor' },
    { label: 'Supply Air Flow Sensor', value: 'brick:Supply_Air_Flow_Sensor' },
    { label: 'Discharge Air Flow Sensor', value: 'brick:Discharge_Air_Flow_Sensor' },
    { label: 'Return Air Flow Sensor', value: 'brick:Return_Air_Flow_Sensor' },
    { label: 'Exhaust Air Flow Sensor', value: 'brick:Exhaust_Air_Flow_Sensor' },
    { label: 'Outside Air Flow Sensor', value: 'brick:Outside_Air_Flow_Sensor' },
    // Water Flow Sensors
    { label: 'Water Flow Sensor', value: 'brick:Water_Flow_Sensor' },
    { label: 'Chilled Water Flow Sensor', value: 'brick:Chilled_Water_Flow_Sensor' },
    { label: 'Hot Water Flow Sensor', value: 'brick:Hot_Water_Flow_Sensor' },
    { label: 'Condenser Water Flow Sensor', value: 'brick:Condenser_Water_Flow_Sensor' },

    // === HUMIDITY SENSORS ===
    { label: 'Humidity Sensor', value: 'brick:Humidity_Sensor' },
    { label: 'Relative Humidity Sensor', value: 'brick:Relative_Humidity_Sensor' },
    { label: 'Discharge Air Humidity Sensor', value: 'brick:Discharge_Air_Humidity_Sensor' },
    { label: 'Outside Air Humidity Sensor', value: 'brick:Outside_Air_Humidity_Sensor' },
    { label: 'Return Air Humidity Sensor', value: 'brick:Return_Air_Humidity_Sensor' },
    { label: 'Supply Air Humidity Sensor', value: 'brick:Supply_Air_Humidity_Sensor' },
    { label: 'Zone Air Humidity Sensor', value: 'brick:Zone_Air_Humidity_Sensor' },

    // === AIR QUALITY SENSORS ===
    { label: 'Air Quality Sensor', value: 'brick:Air_Quality_Sensor' },
    { label: 'CO2 Sensor', value: 'brick:CO2_Sensor' },
    { label: 'CO2 Level Sensor', value: 'brick:CO2_Level_Sensor' },
    { label: 'CO Sensor', value: 'brick:CO_Sensor' },
    { label: 'PM2.5 Sensor', value: 'brick:PM2.5_Sensor' },
    { label: 'TVOC Sensor', value: 'brick:TVOC_Sensor' },

    // === POSITION SENSORS ===
    { label: 'Position Sensor', value: 'brick:Position_Sensor' },
    { label: 'Damper Position Sensor', value: 'brick:Damper_Position_Sensor' },
    { label: 'Valve Position Sensor', value: 'brick:Valve_Position_Sensor' },

    // === SPEED/FREQUENCY SENSORS ===
    { label: 'Speed Sensor', value: 'brick:Speed_Sensor' },
    { label: 'Motor Speed Sensor', value: 'brick:Motor_Speed_Sensor' },
    { label: 'Frequency Sensor', value: 'brick:Frequency_Sensor' },

    // === ELECTRICAL SENSORS ===
    { label: 'Current Sensor', value: 'brick:Current_Sensor' },
    { label: 'Voltage Sensor', value: 'brick:Voltage_Sensor' },
    { label: 'Power Sensor', value: 'brick:Power_Sensor' },
    { label: 'Active Power Sensor', value: 'brick:Active_Power_Sensor' },
    { label: 'Reactive Power Sensor', value: 'brick:Reactive_Power_Sensor' },
    { label: 'Energy Sensor', value: 'brick:Energy_Sensor' },
    { label: 'Power Factor Sensor', value: 'brick:Power_Factor_Sensor' },

    // === OCCUPANCY/CONTACT ===
    { label: 'Occupancy Sensor', value: 'brick:Occupancy_Sensor' },
    { label: 'PIR Sensor', value: 'brick:PIR_Sensor' },
    { label: 'Contact Sensor', value: 'brick:Contact_Sensor' },

    // === SETPOINTS ===
    { label: 'Setpoint', value: 'brick:Setpoint' },
    { label: 'Temperature Setpoint', value: 'brick:Temperature_Setpoint' },
    { label: 'Air Temperature Setpoint', value: 'brick:Air_Temperature_Setpoint' },
    { label: 'Cooling Temperature Setpoint', value: 'brick:Cooling_Temperature_Setpoint' },
    { label: 'Heating Temperature Setpoint', value: 'brick:Heating_Temperature_Setpoint' },
    { label: 'Zone Air Temperature Setpoint', value: 'brick:Zone_Air_Temperature_Setpoint' },
    { label: 'Supply Air Temperature Setpoint', value: 'brick:Supply_Air_Temperature_Setpoint' },
    { label: 'Discharge Air Temperature Setpoint', value: 'brick:Discharge_Air_Temperature_Setpoint' },
    { label: 'Pressure Setpoint', value: 'brick:Pressure_Setpoint' },
    { label: 'Static Pressure Setpoint', value: 'brick:Static_Pressure_Setpoint' },
    { label: 'Supply Air Static Pressure Setpoint', value: 'brick:Supply_Air_Static_Pressure_Setpoint' },
    { label: 'Flow Setpoint', value: 'brick:Flow_Setpoint' },
    { label: 'Air Flow Setpoint', value: 'brick:Air_Flow_Setpoint' },
    { label: 'Humidity Setpoint', value: 'brick:Humidity_Setpoint' },
    { label: 'CO2 Setpoint', value: 'brick:CO2_Setpoint' },
    { label: 'Speed Setpoint', value: 'brick:Speed_Setpoint' },

    // === COMMANDS ===
    { label: 'Command', value: 'brick:Command' },
    { label: 'Position Command', value: 'brick:Position_Command' },
    { label: 'Damper Position Command', value: 'brick:Damper_Position_Command' },
    { label: 'Valve Position Command', value: 'brick:Valve_Position_Command' },
    { label: 'Speed Command', value: 'brick:Speed_Command' },
    { label: 'Frequency Command', value: 'brick:Frequency_Command' },
    { label: 'On Off Command', value: 'brick:On_Off_Command' },
    { label: 'Enable Command', value: 'brick:Enable_Command' },
    { label: 'Reset Command', value: 'brick:Reset_Command' },

    // === STATUS ===
    { label: 'Status', value: 'brick:Status' },
    { label: 'On Off Status', value: 'brick:On_Off_Status' },
    { label: 'Run Status', value: 'brick:Run_Status' },
    { label: 'Enable Status', value: 'brick:Enable_Status' },
    { label: 'Fault Status', value: 'brick:Fault_Status' },
    { label: 'Motor Status', value: 'brick:Motor_Status' },
    { label: 'Occupancy Status', value: 'brick:Occupancy_Status' },
    { label: 'Mode Status', value: 'brick:Mode_Status' },
    { label: 'Operating Mode Status', value: 'brick:Operating_Mode_Status' },

    // === ALARMS ===
    { label: 'Alarm', value: 'brick:Alarm' },
    { label: 'High Temperature Alarm', value: 'brick:High_Temperature_Alarm' },
    { label: 'Low Temperature Alarm', value: 'brick:Low_Temperature_Alarm' },
    { label: 'High Pressure Alarm', value: 'brick:High_Pressure_Alarm' },
    { label: 'Low Pressure Alarm', value: 'brick:Low_Pressure_Alarm' },
    { label: 'Filter Alarm', value: 'brick:Filter_Alarm' },
    { label: 'Smoke Alarm', value: 'brick:Smoke_Alarm' },
    { label: 'Fire Alarm', value: 'brick:Fire_Alarm' },

    // === GENERIC ===
    { label: 'Point', value: 'brick:Point' },
    { label: 'Sensor', value: 'brick:Sensor' },
  ];

  // Device BRICK classes - comprehensive BRICK 1.4 classes
  const deviceBrickClasses = [
    // === TEMPERATURE SENSORS ===
    { label: 'Temperature Sensor', value: 'brick:Temperature_Sensor' },
    { label: 'Air Temperature Sensor', value: 'brick:Air_Temperature_Sensor' },
    { label: 'Discharge Air Temperature Sensor', value: 'brick:Discharge_Air_Temperature_Sensor' },
    { label: 'Mixed Air Temperature Sensor', value: 'brick:Mixed_Air_Temperature_Sensor' },
    { label: 'Outside Air Temperature Sensor', value: 'brick:Outside_Air_Temperature_Sensor' },
    { label: 'Return Air Temperature Sensor', value: 'brick:Return_Air_Temperature_Sensor' },
    { label: 'Supply Air Temperature Sensor', value: 'brick:Supply_Air_Temperature_Sensor' },
    { label: 'Zone Air Temperature Sensor', value: 'brick:Zone_Air_Temperature_Sensor' },
    { label: 'Water Temperature Sensor', value: 'brick:Water_Temperature_Sensor' },
    { label: 'Chilled Water Temperature Sensor', value: 'brick:Chilled_Water_Temperature_Sensor' },
    { label: 'Chilled Water Supply Temperature Sensor', value: 'brick:Chilled_Water_Supply_Temperature_Sensor' },
    { label: 'Chilled Water Return Temperature Sensor', value: 'brick:Chilled_Water_Return_Temperature_Sensor' },
    { label: 'Hot Water Temperature Sensor', value: 'brick:Hot_Water_Temperature_Sensor' },
    { label: 'Condenser Water Temperature Sensor', value: 'brick:Condenser_Water_Temperature_Sensor' },

    // === PRESSURE SENSORS ===
    { label: 'Pressure Sensor', value: 'brick:Pressure_Sensor' },
    { label: 'Differential Pressure Sensor', value: 'brick:Differential_Pressure_Sensor' },
    { label: 'Air Pressure Sensor', value: 'brick:Air_Pressure_Sensor' },
    { label: 'Air Static Pressure Sensor', value: 'brick:Air_Static_Pressure_Sensor' },
    { label: 'Supply Air Static Pressure Sensor', value: 'brick:Supply_Air_Static_Pressure_Sensor' },
    { label: 'Discharge Air Static Pressure Sensor', value: 'brick:Discharge_Air_Static_Pressure_Sensor' },
    { label: 'Filter Differential Pressure Sensor', value: 'brick:Filter_Differential_Pressure_Sensor' },
    { label: 'Water Pressure Sensor', value: 'brick:Water_Pressure_Sensor' },
    { label: 'Chilled Water Differential Pressure Sensor', value: 'brick:Chilled_Water_Differential_Pressure_Sensor' },

    // === FLOW SENSORS ===
    { label: 'Flow Sensor', value: 'brick:Flow_Sensor' },
    { label: 'Air Flow Sensor', value: 'brick:Air_Flow_Sensor' },
    { label: 'Supply Air Flow Sensor', value: 'brick:Supply_Air_Flow_Sensor' },
    { label: 'Return Air Flow Sensor', value: 'brick:Return_Air_Flow_Sensor' },
    { label: 'Exhaust Air Flow Sensor', value: 'brick:Exhaust_Air_Flow_Sensor' },
    { label: 'Water Flow Sensor', value: 'brick:Water_Flow_Sensor' },
    { label: 'Chilled Water Flow Sensor', value: 'brick:Chilled_Water_Flow_Sensor' },
    { label: 'Hot Water Flow Sensor', value: 'brick:Hot_Water_Flow_Sensor' },

    // === HUMIDITY SENSORS ===
    { label: 'Humidity Sensor', value: 'brick:Humidity_Sensor' },
    { label: 'Relative Humidity Sensor', value: 'brick:Relative_Humidity_Sensor' },
    { label: 'Outside Air Humidity Sensor', value: 'brick:Outside_Air_Humidity_Sensor' },
    { label: 'Return Air Humidity Sensor', value: 'brick:Return_Air_Humidity_Sensor' },
    { label: 'Zone Air Humidity Sensor', value: 'brick:Zone_Air_Humidity_Sensor' },

    // === AIR QUALITY SENSORS ===
    { label: 'Air Quality Sensor', value: 'brick:Air_Quality_Sensor' },
    { label: 'CO2 Sensor', value: 'brick:CO2_Sensor' },
    { label: 'CO Sensor', value: 'brick:CO_Sensor' },
    { label: 'Smoke Detector', value: 'brick:Smoke_Detector' },
    { label: 'Occupancy Sensor', value: 'brick:Occupancy_Sensor' },
    { label: 'PIR Sensor', value: 'brick:PIR_Sensor' },

    // === AIR QUALITY & TREATMENT ===
    { label: 'Filter', value: 'brick:Filter' },
    { label: 'Pre Filter', value: 'brick:Pre_Filter' },
    { label: 'Final Filter', value: 'brick:Final_Filter' },
    { label: 'HEPA Filter', value: 'brick:HEPA_Filter' },
    { label: 'UV Light', value: 'brick:UV_Light' },

    // === COILS & HEAT TRANSFER ===
    { label: 'Coil', value: 'brick:Coil' },
    { label: 'Cooling Coil', value: 'brick:Cooling_Coil' },
    { label: 'Heating Coil', value: 'brick:Heating_Coil' },
    { label: 'Hot Water Coil', value: 'brick:Hot_Water_Coil' },
    { label: 'Chilled Water Coil', value: 'brick:Chilled_Water_Coil' },
    { label: 'Preheat Coil', value: 'brick:Preheat_Coil' },
    { label: 'Reheat Coil', value: 'brick:Reheat_Coil' },
    { label: 'Heat Wheel', value: 'brick:Heat_Wheel' },
    { label: 'Heat Recovery Unit', value: 'brick:Heat_Recovery_Unit' },
    { label: 'Electric Heater', value: 'brick:Electric_Heater' },
    { label: 'Gas Heater', value: 'brick:Gas_Heater' },
    { label: 'Heat Exchanger', value: 'brick:Heat_Exchanger' },

    // === HUMIDITY CONTROL ===
    { label: 'Humidifier', value: 'brick:Humidifier' },
    { label: 'Dehumidifier', value: 'brick:Dehumidifier' },
    { label: 'Economizer', value: 'brick:Economizer' },

    // === DAMPERS ===
    { label: 'Damper', value: 'brick:Damper' },
    { label: 'Supply Air Damper', value: 'brick:Supply_Air_Damper' },
    { label: 'Return Air Damper', value: 'brick:Return_Air_Damper' },
    { label: 'Exhaust Air Damper', value: 'brick:Exhaust_Air_Damper' },
    { label: 'Outside Air Damper', value: 'brick:Outside_Air_Damper' },
    { label: 'Mixed Air Damper', value: 'brick:Mixed_Air_Damper' },
    { label: 'Bypass Damper', value: 'brick:Bypass_Damper' },
    { label: 'Fire Damper', value: 'brick:Fire_Damper' },
    { label: 'Smoke Damper', value: 'brick:Smoke_Damper' },

    // === ACTUATORS ===
    { label: 'Actuator', value: 'brick:Actuator' },
    { label: 'Damper Actuator', value: 'brick:Damper_Actuator' },
    { label: 'Valve Actuator', value: 'brick:Valve_Actuator' },
    { label: 'Variable Frequency Drive', value: 'brick:Variable_Frequency_Drive' },

    // === FANS ===
    { label: 'Fan', value: 'brick:Fan' },
    { label: 'Supply Fan', value: 'brick:Supply_Fan' },
    { label: 'Return Fan', value: 'brick:Return_Fan' },
    { label: 'Exhaust Fan', value: 'brick:Exhaust_Fan' },
    { label: 'Relief Fan', value: 'brick:Relief_Fan' },
    { label: 'Condenser Fan', value: 'brick:Condenser_Fan' },
    { label: 'Cooling Tower Fan', value: 'brick:Cooling_Tower_Fan' },

    // === VALVES ===
    { label: 'Valve', value: 'brick:Valve' },
    { label: 'Hot Water Valve', value: 'brick:Hot_Water_Valve' },
    { label: 'Chilled Water Valve', value: 'brick:Chilled_Water_Valve' },
    { label: 'Condenser Water Valve', value: 'brick:Condenser_Water_Valve' },
    { label: 'Bypass Valve', value: 'brick:Bypass_Valve' },
    { label: 'Isolation Valve', value: 'brick:Isolation_Valve' },

    // === PUMPS ===
    { label: 'Pump', value: 'brick:Pump' },
    { label: 'Chilled Water Pump', value: 'brick:Chilled_Water_Pump' },
    { label: 'Hot Water Pump', value: 'brick:Hot_Water_Pump' },
    { label: 'Condenser Water Pump', value: 'brick:Condenser_Water_Pump' },

    // === MOTOR CONTROLLERS ===
    { label: 'DOL Motor Controller', value: 'brick:DOL_Motor_Controller' },
    { label: 'VSD Motor Controller', value: 'brick:VSD_Motor_Controller' },
    { label: 'EC Motor Controller', value: 'brick:EC_Motor_Controller' },

    // Controllers
    { label: 'Controller', value: 'brick:Controller' },
    { label: 'Thermostat', value: 'brick:Thermostat' },
    { label: 'Building Management System', value: 'brick:Building_Management_System' },

    // Valves
    { label: 'Hot Water Valve', value: 'brick:Hot_Water_Valve' },
    { label: 'Chilled Water Valve', value: 'brick:Chilled_Water_Valve' },

    // Meters
    { label: 'Power Meter', value: 'brick:Power_Meter' },
    { label: 'Energy Meter', value: 'brick:Energy_Meter' },
    { label: 'Flow Meter', value: 'brick:Flow_Meter' },
  ];

  // Service type options for switchboards
  const serviceTypeOptions = [
    'HVAC',
    'Lighting',
    'Power',
    'Emergency',
    'UPS',
    'General Services',
  ];

  // Controller type options
  const controllerTypeOptions = [
    'BACnet',
    'Modbus',
    'LON',
    'KNX',
    'DALI',
    'Proprietary',
  ];

  // Parent role options for compound devices/equipment
  const parentRoleOptions = [
    { label: 'Controller (Primary)', value: 'controller' },
    { label: 'Actuator (Primary)', value: 'actuator' },
    { label: 'Equipment (Primary)', value: 'equipment' },
  ];

  // Relationship options for compound assemblies
  const relationshipOptions = [
    { label: 'Controls', value: 'controls' },
    { label: 'Has Part', value: 'hasPart' },
    { label: 'Is Part Of', value: 'isPartOf' },
    { label: 'Feeds', value: 'feeds' },
    { label: 'Is Fed By', value: 'isFedBy' },
  ];

  // Zone Section Type Options for AHU internal zones
  const zoneSectionTypeOptions = [
    { label: 'Mixing Zone', value: 'mixing' },
    { label: 'Hot Deck Zone', value: 'hot_deck' },
    { label: 'Cold Deck Zone', value: 'cold_deck' },
    { label: 'Bypass Zone', value: 'bypass' },
    { label: 'Variable Zone', value: 'variable' },
  ];

  // Compound BRICK class options (for assemblies)
  const compoundDeviceBrickClasses = [
    { label: 'Valve Assembly', value: 'brick:Valve_Assembly' },
    { label: 'Actuator Assembly', value: 'brick:Actuator_Assembly' },
    { label: 'Controller Assembly', value: 'brick:Controller_Assembly' },
    { label: 'Sensor Assembly', value: 'brick:Sensor_Assembly' },
    { label: 'Device Assembly', value: 'brick:Device_Assembly' },
  ];

  const compoundEquipmentBrickClasses = [
    { label: 'Valve Assembly', value: 'brick:Valve_Assembly' },
    { label: 'Fan Assembly', value: 'brick:Fan_Assembly' },
    { label: 'Pump Assembly', value: 'brick:Pump_Assembly' },
    { label: 'Controller Assembly', value: 'brick:Controller_Assembly' },
    { label: 'Equipment Assembly', value: 'brick:Equipment_Assembly' },
  ];

  /**
   * Get compound BRICK classes based on item type
   * @param {string} itemType - Either 'device' or 'equipment'
   * @returns {Array} Array of compound class options
   */
  function getCompoundBrickClasses(itemType) {
    return itemType === 'device'
      ? compoundDeviceBrickClasses
      : compoundEquipmentBrickClasses;
  }

  /**
   * Template type filter options (derived from equipment classes)
   */
  const templateTypeOptions = equipmentBrickClasses.map((item) => ({
    label: item.label,
    value: item.value,
  }));

  /**
   * Filter device brick classes based on a template's brick_class constraint.
   * When a device template specifies a brick_class (e.g., brick:Humidity_Sensor),
   * only that class and its subclasses should be selectable for devices
   * created from that template.
   *
   * @param {string|null} templateBrickClass - The BRICK class from the device template
   * @returns {Array} Filtered array of brick class options (label/value pairs)
   *
   * @example
   * // Template specifies brick:Humidity_Sensor
   * const options = getFilteredDeviceBrickClasses('brick:Humidity_Sensor');
   * // Returns: Humidity_Sensor, Relative_Humidity_Sensor, Outside_Air_Humidity_Sensor, etc.
   */
  function getFilteredDeviceBrickClasses(templateBrickClass) {
    // If no template constraint, return all device brick classes
    if (!templateBrickClass) {
      return deviceBrickClasses;
    }

    const normalizedConstraint = normalizeBrickClass(templateBrickClass);
    if (!normalizedConstraint) {
      return deviceBrickClasses;
    }

    // Get all valid subclasses (including the parent class itself)
    const validClasses = getSubclasses(normalizedConstraint, true);
    const validSet = new Set(validClasses);

    // Filter the device brick classes to only include valid subclasses
    const filteredFromStatic = deviceBrickClasses.filter((option) => {
      const normalized = normalizeBrickClass(option.value);
      return normalized && validSet.has(normalized);
    });

    // If we have filtered options, return them
    if (filteredFromStatic.length > 0) {
      return filteredFromStatic;
    }

    // If no matches in static list, build options from hierarchy
    // This ensures even if our static list is incomplete, we still show valid options
    return validClasses.map((cls) => ({
      label: getDisplayName(cls),
      value: cls,
    }));
  }

  /**
   * Filter point brick classes based on a constraint class.
   * Similar to device filtering but for points.
   *
   * @param {string|null} constraintBrickClass - The BRICK class to filter by
   * @returns {Array} Filtered array of brick class options (label/value pairs)
   */
  function getFilteredPointBrickClasses(constraintBrickClass) {
    // If no constraint, return all point brick classes
    if (!constraintBrickClass) {
      return pointBrickClasses;
    }

    const normalizedConstraint = normalizeBrickClass(constraintBrickClass);
    if (!normalizedConstraint) {
      return pointBrickClasses;
    }

    // Get all valid subclasses (including the parent class itself)
    const validClasses = getSubclasses(normalizedConstraint, true);
    const validSet = new Set(validClasses);

    // Filter the point brick classes to only include valid subclasses
    const filteredFromStatic = pointBrickClasses.filter((option) => {
      const normalized = normalizeBrickClass(option.value);
      return normalized && validSet.has(normalized);
    });

    // If we have filtered options, return them
    if (filteredFromStatic.length > 0) {
      return filteredFromStatic;
    }

    // If no matches in static list, build options from hierarchy
    return validClasses.map((cls) => ({
      label: getDisplayName(cls),
      value: cls,
    }));
  }

  return {
    // BRICK Classes
    buildingBrickClasses,
    levelBrickClasses,
    spaceBrickClasses,
    equipmentBrickClasses,
    pointBrickClasses,
    deviceBrickClasses,

    // Compound Classes
    compoundDeviceBrickClasses,
    compoundEquipmentBrickClasses,
    getCompoundBrickClasses,

    // Service & Controller Options
    serviceTypeOptions,
    controllerTypeOptions,

    // Relationship Options
    parentRoleOptions,
    relationshipOptions,

    // Zone Options
    zoneSectionTypeOptions,

    // Template Options
    templateTypeOptions,

    // Filtering Functions (for template constraints)
    getFilteredDeviceBrickClasses,
    getFilteredPointBrickClasses,

    // Re-export hierarchy utilities for direct use
    getSubclasses,
    normalizeBrickClass,
    getDisplayName,
  };
}
